import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getPayments } from "@/api/admin.api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CreditCard, ChevronLeft, ChevronRight } from "lucide-react";

const STATUS_BADGE: Record<string, { label: string; variant: "default" | "secondary" | "destructive" }> = {
  success: { label: "Basarili", variant: "default" },
  pending: { label: "Bekliyor", variant: "secondary" },
  failed: { label: "Basarisiz", variant: "destructive" },
};

const PAGE_SIZE = 20;

export function PaymentsPage() {
  const [statusFilter, setStatusFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [page, setPage] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-payments", statusFilter, userFilter, page],
    queryFn: () => getPayments({
      status: statusFilter || undefined,
      userId: userFilter || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    refetchInterval: 30000,
  });

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  return (
    <div className="max-w-4xl">
      <h1 className="mb-6 text-2xl font-bold">Odemeler</h1>

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <Input
          placeholder="User ID ile filtrele..."
          value={userFilter}
          onChange={(e) => { setUserFilter(e.target.value.trim()); setPage(0); }}
          className="h-8 w-56"
        />
        <div className="flex gap-2">
          {["", "success", "pending", "failed"].map((s) => (
            <Badge
              key={s}
              variant={statusFilter === s ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => { setStatusFilter(s); setPage(0); }}
            >
              {s === "" ? "Tumu" : STATUS_BADGE[s]?.label || s}
            </Badge>
          ))}
        </div>
      </div>

      {isLoading ? (
        <p className="py-8 text-center text-muted-foreground">Yukleniyor...</p>
      ) : !data?.payments.length ? (
        <div className="flex flex-col items-center justify-center py-16">
          <CreditCard className="mb-4 size-12 text-muted-foreground" />
          <p className="text-muted-foreground">Odeme bulunamadi</p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {data.payments.map((p) => {
              const badge = STATUS_BADGE[p.status] || STATUS_BADGE.pending;
              return (
                <Card key={p._id}>
                  <CardContent className="flex items-center gap-4 py-3">
                    <CreditCard className="size-5 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{p.amount} {p.currency}</span>
                        <Badge variant={badge.variant} className="text-xs">{badge.label}</Badge>
                        <Badge variant="secondary" className="text-xs">{p.planType} / {p.period === "yearly" ? "Yillik" : "Aylik"}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {p.userId?.name} ({p.userId?.email})
                        {p.venueId && <> — {p.venueId.name} {p.venueId.venueNumber ? `#${p.venueId.venueNumber}` : ""}</>}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(p.createdAt).toLocaleString("tr-TR")}
                        {p.iyzicoPaymentId && <> — iyzico: {p.iyzicoPaymentId}</>}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-center gap-4">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
                <ChevronLeft className="size-4" />
              </Button>
              <span className="text-sm text-muted-foreground">{page + 1} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                <ChevronRight className="size-4" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
